import {
  AfterContentInit,
  Component,
  ContentChildren,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  TemplateRef,
} from '@angular/core';
import { DATE_TIME_FORMAT } from '@app/app.constants';
import { AppService } from '@app/app.service';
import { AuthenticationService } from '@app/auth/services/authentication.service';
import { RouterService } from '@app/routing/router.service';
import { TranscludeDirective } from '@app/shared/directives/transclude.directive';
import { ErrorTransformerService } from '@app/shared/services/error-transformer.service';
import { FileService } from '@app/shared/services/file.service';
import { DateUtils } from '@app/utils/date';
import { PortalError } from '@app/utils/error/portal-error';
import { forkJoin } from 'rxjs';
import { first } from 'rxjs/operators';

@Component({
  selector: 'portal-error-header',
  templateUrl: './error-header.component.html',
  styles: [],
  standalone: false,
})
export class ErrorHeaderComponent implements AfterContentInit, OnChanges, OnInit {
  @Input()
  className: string = 'errors__header';

  @Input()
  dismissible: boolean;

  @Input()
  errors!: PortalError[];

  @Input()
  hideCollapse: boolean = false;

  @Input()
  summary?: TemplateRef<any>;

  @Input()
  title?: TemplateRef<any>;

  @Input()
  hideWarningIcon: boolean = false;

  @Output()
  hide = new EventEmitter<void>();

  @Output()
  toggle = new EventEmitter<void>();

  @ContentChildren(TranscludeDirective)
  transclusions: QueryList<TranscludeDirective>;

  errorCount = 0;
  rawErrors: string = '';

  constructor(
    private appService: AppService,
    private authenticationService: AuthenticationService,
    private errorTransformerService: ErrorTransformerService,
    private fileService: FileService,
    private routerService: RouterService
  ) {}

  ngAfterContentInit() {
    if (this.summary == null) {
      this.summary = this.transclusions?.find((item) => item.id === 'summary')?.templateRef;
    }

    if (this.title == null) {
      this.title = this.transclusions?.find((item) => item.id === 'title')?.templateRef;
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if ('errors' in changes) {
      this.updateState(changes.errors.currentValue ?? []);
    }
  }

  ngOnInit() {
    this.updateState(this.errors ?? []);
  }

  downloadErrors(): void {
    this.fileService.saveAsFile(this.rawErrors, 'common.errors.export.fileName');
  }

  private updateState(errors: PortalError[]) {
    const flattened: PortalError[] = this.errorTransformerService.flattenErrors(errors);
    this.errorCount = flattened.length;

    forkJoin([
      this.appService.info.data$.pipe(first()),
      this.appService.branding.data$.pipe(first()),
      this.authenticationService.authUser$.pipe(first()),
    ]).subscribe(([appInfo, branding, user]) => {
      this.rawErrors = JSON.stringify(
        {
          appInfo,
          branding,
          date: DateUtils.format(new Date(), DATE_TIME_FORMAT),
          errors: this.errors,
          route: this.routerService.currentRouteData,
          user,
          userAgent: navigator?.userAgent ?? 'no user-agent found',
        },
        undefined,
        2
      );
    });
  }
}
